import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../../_augmentt/selectors/customers'
import { getEmployees } from '../crud/employees.crud'

/**
 * Centralized hook for fetching employees with fallback logic
 *
 * This hook provides a consistent way to get employee lists across all modals.
 * It prioritizes the `allEmployees` prop if provided, otherwise fetches employees automatically.
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.allEmployees - Optional prop array of employees (if provided, will be used instead of fetching)
 * @param {Object} options.selectedCustomer - Optional customer object (will extract id from it)
 * @param {number} options.customerId - Optional customer ID (alternative to selectedCustomer)
 * @param {boolean} options.show - Whether the modal/form is shown (triggers fetch when true)
 * @param {Array} options.select - Optional array of fields to select (defaults to basic fields)
 * @returns {Object} - { allEmployees: Array, isFetching: boolean }
 */
const DEFAULT_SELECT = ['id', 'email', 'first_name', 'last_name', 'display_name', 'ignored']

const useAllEmployees = ({
  allEmployees: propAllEmployees = [],
  selectedCustomer,
  customerId: propCustomerId,
  show = true,
  select = DEFAULT_SELECT,
} = {}) => {
  const [fetchedAllEmployees, setFetchedAllEmployees] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const selectRef = useRef(select)
  const selectStringRef = useRef(JSON.stringify(select))

  // Get customerId from Redux if not provided
  const reduxCustomerId = useSelector(getSelectedCustomerId)
  const customerId = propCustomerId || selectedCustomer?.id || reduxCustomerId

  // Update ref when select changes (in effect to avoid render-time updates)
  useEffect(() => {
    const selectString = JSON.stringify(select)
    if (selectStringRef.current !== selectString) {
      selectRef.current = select
      selectStringRef.current = selectString
    }
  }, [select])

  // Use prop allEmployees if available, otherwise use fetched employees
  const allEmployees = propAllEmployees.length > 0 ? propAllEmployees : fetchedAllEmployees

  // Fetch employees if not provided via props
  // Note: select is intentionally NOT in the dependency array - we use selectRef.current instead
  // to avoid infinite loops when select array is recreated on each render
  useEffect(() => {
    if (show && customerId && propAllEmployees.length === 0) {
      setIsFetching(true)
      const fetchEmployees = async () => {
        try {
          const response = await getEmployees(customerId, { select: selectRef.current })
          setFetchedAllEmployees(response?.data || [])
        } catch (error) {
          console.error('[useAllEmployees] Error fetching employees:', error)
          setFetchedAllEmployees([])
        } finally {
          setIsFetching(false)
        }
      }
      fetchEmployees()
    } else if (!show) {
      // Reset when modal/form closes
      setFetchedAllEmployees([])
      setIsFetching(false)
    } else {
      setIsFetching(false)
    }
  }, [show, customerId, propAllEmployees.length])

  return {
    allEmployees,
    isFetching,
  }
}

export default useAllEmployees
